/**
 * Admin KYC Review page — kyc-1-1 (AC:9)
 *
 * Lists pending/under_review KYC documents and allows approve/reject.
 * Route: /admin/kyc
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  User,
  Building2,
  AlertCircle,
  Loader2,
  ExternalLink,
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

interface KycDocument {
  id: number;
  entity_type: 'org' | 'user';
  entity_id: number;
  doc_type: string;
  filename: string;
  file_size_bytes: number;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'expired';
  reviewed_by: number | null;
  reviewed_at: string | null;
  expiry_date: string | null;
  rejection_reason: string | null;
  uploaded_by: number;
  created_at: string;
}

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  pending:      { label: 'Pending',      className: 'bg-yellow-100 text-yellow-800' },
  under_review: { label: 'Under Review', className: 'bg-blue-100 text-blue-800' },
  approved:     { label: 'Approved',     className: 'bg-green-100 text-green-800' },
  rejected:     { label: 'Rejected',     className: 'bg-red-100 text-red-800' },
  expired:      { label: 'Expired',      className: 'bg-gray-100 text-gray-600' },
};

function formatDocType(dt: string): string {
  return dt.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

async function fetchPendingDocs(token: string): Promise<KycDocument[]> {
  const res = await fetch(`${API_URL}/api/v1/admin/kyc/pending`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch pending KYC documents');
  return res.json();
}

async function approveDoc(token: string, docId: number, expiryDate?: string): Promise<void> {
  const res = await fetch(`${API_URL}/api/v1/admin/kyc/${docId}/approve`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ expiry_date: expiryDate || null }),
  });
  if (!res.ok) throw new Error('Approve failed');
}

async function rejectDoc(token: string, docId: number, reason: string): Promise<void> {
  const res = await fetch(`${API_URL}/api/v1/admin/kyc/${docId}/reject`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ rejection_reason: reason }),
  });
  if (!res.ok) throw new Error('Reject failed');
}

async function getPreviewUrl(token: string, docId: number): Promise<string> {
  const res = await fetch(`${API_URL}/api/v1/admin/kyc/${docId}/preview-url`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to get preview URL');
  const data = await res.json();
  return data.url;
}

const AdminKycReview: React.FC = () => {
  const token = localStorage.getItem('access_token') || '';
  const queryClient = useQueryClient();

  const [rejectDocId, setRejectDocId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approveDocId, setApproveDocId] = useState<number | null>(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [previewLoading, setPreviewLoading] = useState<number | null>(null);

  const { data: docs, isLoading, error } = useQuery({
    queryKey: ['admin-kyc-pending'],
    queryFn: () => fetchPendingDocs(token),
  });

  const approveMutation = useMutation({
    mutationFn: ({ docId, expiry }: { docId: number; expiry?: string }) =>
      approveDoc(token, docId, expiry),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-kyc-pending'] });
      setApproveDocId(null);
      setExpiryDate('');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ docId, reason }: { docId: number; reason: string }) =>
      rejectDoc(token, docId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-kyc-pending'] });
      setRejectDocId(null);
      setRejectReason('');
    },
  });

  const handlePreview = async (docId: number) => {
    setPreviewLoading(docId);
    try {
      const url = await getPreviewUrl(token, docId);
      window.open(url, '_blank', 'noopener');
    } catch {
      alert('Failed to generate preview link. GCS may not be configured.');
    } finally {
      setPreviewLoading(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-red-500 flex items-center gap-2">
        <AlertCircle className="h-5 w-5" /> Failed to load pending documents
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="h-7 w-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">KYC Review Queue</h1>
          <p className="text-gray-500 text-sm">
            Review and approve/reject FICA KYC documents for orgs and users
          </p>
        </div>
      </div>

      {(!docs || docs.length === 0) ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
          <p className="text-green-700 font-medium">Queue is clear</p>
          <p className="text-green-600 text-sm mt-1">No documents pending review</p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">{docs.length} document{docs.length !== 1 ? 's' : ''} pending review (oldest first)</p>

          {docs.map(doc => {
            const badge = STATUS_BADGE[doc.status];
            return (
              <div key={doc.id} className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      {doc.entity_type === 'org' ? (
                        <Building2 className="h-5 w-5 text-gray-600" />
                      ) : (
                        <User className="h-5 w-5 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{formatDocType(doc.doc_type)}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.className}`}>
                          {badge.label}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        <span className="capitalize">{doc.entity_type}</span> ID {doc.entity_id}
                        &nbsp;·&nbsp;{doc.filename}
                        &nbsp;·&nbsp;{formatBytes(doc.file_size_bytes)}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        Uploaded {new Date(doc.created_at).toLocaleString()} by user #{doc.uploaded_by}
                      </div>
                      {doc.expiry_date && (
                        <div className="text-xs text-amber-600 mt-0.5">
                          Expiry: {doc.expiry_date}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handlePreview(doc.id)}
                      disabled={previewLoading === doc.id}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      {previewLoading === doc.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <ExternalLink className="h-3.5 w-3.5" />
                      )}
                      Preview
                    </button>
                    <button
                      onClick={() => setApproveDocId(doc.id)}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                    >
                      <CheckCircle className="h-3.5 w-3.5" />
                      Approve
                    </button>
                    <button
                      onClick={() => { setRejectDocId(doc.id); setRejectReason(''); }}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Approve modal */}
      {approveDocId !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Approve Document
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Optionally set an expiry date for this document (e.g. 12 months for proof of address).
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (optional)</label>
            <input
              type="date"
              value={expiryDate}
              onChange={e => setExpiryDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:outline-none"
            />
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => approveMutation.mutate({ docId: approveDocId, expiry: expiryDate || undefined })}
                disabled={approveMutation.isPending}
                className="flex-1 bg-green-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {approveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Confirm Approval
              </button>
              <button
                onClick={() => { setApproveDocId(null); setExpiryDate(''); }}
                className="flex-1 border border-gray-300 rounded-lg py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
            {approveMutation.isError && (
              <p className="text-red-500 text-xs mt-2">Approval failed. Please try again.</p>
            )}
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectDocId !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" />
              Reject Document
            </h2>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rejection Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Explain why the document is being rejected (visible to user)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none resize-none"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => rejectMutation.mutate({ docId: rejectDocId, reason: rejectReason })}
                disabled={rejectMutation.isPending || !rejectReason.trim()}
                className="flex-1 bg-red-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {rejectMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Confirm Rejection
              </button>
              <button
                onClick={() => { setRejectDocId(null); setRejectReason(''); }}
                className="flex-1 border border-gray-300 rounded-lg py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
            {rejectMutation.isError && (
              <p className="text-red-500 text-xs mt-2">Rejection failed. Please try again.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminKycReview;
